// Import enumerations.
import {
	FileExtension,
} from '@app/enumerations';
// Import crypt-js.
import * as CryptoJS from 'crypto-js';
import { v4 as uuidv4 } from 'uuid';

export default class StringService {

	private static instance: StringService;

	private constructor() {

	}

	/**
	* Get a singleton instance of StringService.
	*/
	public static getInstance(): StringService {
		if (!StringService.instance) {
			StringService.instance = new StringService();
		}
		return StringService.instance;
	}

	/**
	 *  Creates a randomized guid. 
	 */
	public create_UUID(): string {
		var dt = new Date().getTime();
		var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			var r = (dt + Math.random() * 16) % 16 | 0;
			dt = Math.floor(dt / 16);
			return (c == 'x' ? r : (r & 0x3 | 0x8)).toString(16);
		});
		return uuid;
	}

	/**
	 * Creates hash value based on object value.
	 * @param obj
	 */
	public hashObject(obj: object | undefined): string {
		let val: string = this.create_UUID();
		if (obj !== undefined) {
			val = JSON.stringify(obj);
		}
		const hash = CryptoJS.SHA1(val).toString();
		const hexHash = hash.slice(0, 32);

		return uuidv4({ random: Buffer.from(hexHash, "hex") });
	}

	/**
	 * Calculates the similarity score between two string values.
	 * @param query1
	 * @param query2
	 */
	public editDistance(query1: string, query2: string): number {
		let m = query1.length;
		let n = query2.length;

		let v: Array<Array<number>> = new Array<Array<number>>(m);

		for (let i: number = 0; i <= m; i++) {
			v[i] = new Array<number>(n);
			v[i][0] = i;
		}

		for (let j: number = 0; j <= n; j++) {
			v[0][j] = j;
		}

		for (let i: number = 1; i <= m; i++) {
			for (let j: number = 1; j <= n; j++) {
				if (query1[i - 1] === query2[j - 1]) {
					v[i][j] = v[i - 1][j - 1];
				}
				else {
					v[i][j] = 1 + Math.min(v[i][j - 1], v[i - 1][j], v[i - 1][j - 1]);
				}
			}
		}

		return v[m][n];
	}

	/**
	 * Converts numeric value to a currency text format.
	 * @param num
	 */
	public toCurrencyString(num: number): string {
		var formatter = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		});

		return formatter.format(num);	
	}

	/**
	 * Formats name.
	 * @param firstName
	 * @param lastName
	 */
	public formatName(firstName: string | null | undefined, lastName: string | null | undefined): string {
		let completeName: string = [firstName, lastName]
			.filter((nameToken: string | null | undefined) => nameToken !== undefined && nameToken !== null && nameToken !== String.empty())
			.join(" ");

		return completeName;
	}

	/**
	 * Formats phone number.
	 * @param phoneNumber
	 */
	public formatPhoneNumber(phoneNumber: string | undefined): string {
		let formattedPhoneNumber: string = String.empty();
		if (phoneNumber !== undefined) {
			let cleanedPhoneNumber: string = phoneNumber.replaceAllWithPattern(/\D/, '');
			let matchingPhoneNumberTokens: RegExpMatchArray | null = cleanedPhoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/);

			if (matchingPhoneNumberTokens !== null && matchingPhoneNumberTokens.length === 4) {
				formattedPhoneNumber = `(${matchingPhoneNumberTokens[1]}) ${matchingPhoneNumberTokens[2]}-${matchingPhoneNumberTokens[3]}`
			}
		}

		return formattedPhoneNumber;
	}

	/**
	 * Validates string with email format.
	 * @param email
	 */
	public validateEmailString(email: string): boolean {
		return (/^.+@.+\..+$/i).test(email);
	}

	/**
	 * 
	 * @param docName
	 * @param allowedFileTypes
	 */
	public validateDocumentString(docName: string, allowedFileExtension: FileExtension[]): boolean {
		let docValidation: RegExp = new RegExp(`^.+\.(${allowedFileExtension.join('|')})$`, 'i'); 
		return docValidation.test(docName);
	}
}